import React, { useEffect, useState } from "react";
import axios from "axios";
import { Box, CircularProgress, Typography } from "@mui/material";

import Section from "../../components/gallery/Section";
import SectionHeader from "../../components/gallery/SectionHeader";
import CardRail from "../../components/gallery/CardRail";
import { StoryCard } from "../../components/gallery/Cards";
import { formatShortDate, htmlToText } from "./utils";

/* ------------------------------------------------------------------ */
/* Data (unchanged: same WordPress endpoint, parameters and mapping)   */
/* ------------------------------------------------------------------ */

const WORDPRESS_API = "https://apiblog.leon-gallery.com/wp-json/wp/v2";
const LEGACY_MEDIA_HOST = "188.166.186.239";
const MEDIA_HOST = "apiblog.leon-gallery.com";

function buildPostsUrl(categories, perPage) {
  const params = new URLSearchParams({
    _embed: "wp:featuredmedia,wp:term",
    _fields: "id,date,slug,title,excerpt,_links",
    order: "desc",
    categories,
    per_page: String(perPage),
    page: "1",
  });
  return `${WORDPRESS_API}/posts?${params.toString()}`;
}

/** Normalises a raw WordPress post into exactly what the card needs. */
function toStory(post, fallbackLabel) {
  const media = post._embedded?.["wp:featuredmedia"]?.[0]?.source_url;
  const category = post._embedded?.["wp:term"]?.[0]?.[0]?.name;

  return {
    id: post.id,
    href: `/stories/${post.slug}`,
    image: typeof media === "string" ? media.replace(LEGACY_MEDIA_HOST, MEDIA_HOST) : "",
    date: formatShortDate(post.date),
    label: htmlToText(category) || fallbackLabel,
    title: htmlToText(post.title?.rendered),
    excerpt: htmlToText(post.excerpt?.rendered),
  };
}

/* ------------------------------------------------------------------ */
/* Section                                                             */
/* ------------------------------------------------------------------ */

/**
 * Row of WordPress posts. Used by the "Stories & articles" and "Videos" sections.
 * The section is hidden if the API returns no posts.
 */
function StoryCarousel({
  id,
  eyebrow,
  title,
  categories,
  viewMoreHref,
  viewMoreLabel = "View all",
  fallbackLabel = "Story",
  ctaLabel = "Read the story",
  isVideo = false,
  perPage = 10,
  perView = { sm: 2, md: 3, lg: 3 },
  tone = "paper",
}) {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    axios
      .get(buildPostsUrl(categories, perPage), { signal: controller.signal })
      .then((res) => {
        const posts = Array.isArray(res.data) ? res.data : [];
        setStories(posts.map((post) => toStory(post, fallbackLabel)));
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error(`Failed to load "${title}":`, error.response?.data || error);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [categories, perPage, fallbackLabel, title]);

  if (!loading && stories.length === 0) return null;

  const headingId = id || `home-${String(title).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

  return (
    <Section tone={tone} labelledBy={headingId}>
      <SectionHeader
        id={headingId}
        eyebrow={eyebrow}
        title={title}
        actionLabel={viewMoreLabel}
        actionHref={viewMoreHref}
      />

      {loading ? (
        <Box role="status" aria-live="polite" sx={{ display: "flex", alignItems: "center", gap: 2, py: 8 }}>
          <CircularProgress size={24} color="inherit" />
          <Typography variant="body2">Loading…</Typography>
        </Box>
      ) : (
        <CardRail label={String(title).toLowerCase()} perView={perView} mobileWidth="44%">
          {stories.map((story) => (
            <StoryCard
              key={story.id}
              href={story.href}
              image={story.image}
              eyebrow={[story.date, story.label].filter(Boolean).join("  ·  ")}
              title={story.title}
              excerpt={story.excerpt}
              ctaLabel={ctaLabel}
              isVideo={isVideo}
            />
          ))}
        </CardRail>
      )}
    </Section>
  );
}

export default StoryCarousel;
